import { onBeforeUnmount, onMounted, ref } from 'vue'

/**
 * Shared mobile detection composable.
 *
 * A device is considered "mobile" when either the viewport is at or below the
 * MOBILE_BREAKPOINT or the primary pointer is coarse (touch-first devices).
 * Keeping this logic centralized ensures consistent responsive behavior across
 * components.
 */
const MOBILE_BREAKPOINT = 768

const hasWindow = typeof window !== 'undefined'
const isBelowBreakpoint = (): boolean => {
  if (!hasWindow) return false

  if (window.matchMedia) {
    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches
  }

  return window.innerWidth <= MOBILE_BREAKPOINT
}

const hasCoarsePointer = (): boolean =>
  hasWindow && window.matchMedia
    ? window.matchMedia('(pointer: coarse)').matches
    : false

export function useIsMobile() {
  const isMobile = ref(isBelowBreakpoint() || hasCoarsePointer())

  const updateMobileFlag = (): void => {
    isMobile.value = isBelowBreakpoint() || hasCoarsePointer()
  }

  onMounted(() => {
    updateMobileFlag()
    if (hasWindow) {
      window.addEventListener('resize', updateMobileFlag)
    }
  })

  onBeforeUnmount(() => {
    if (hasWindow) {
      window.removeEventListener('resize', updateMobileFlag)
    }
  })

  return {
    isMobile,
    updateMobileFlag,
  }
}

export { MOBILE_BREAKPOINT }
